/**
 * AuthProvider: JWT in localStorage is the source of truth, httpOnly cookie
 * is the convenience path.
 *
 * Why localStorage-first instead of cookie-first:
 *   The server's httpOnly `auth_token` cookie is set with `SameSite=None +
 *   Secure` for HTTPS cross-site contexts. Mobile browsers frequently drop
 *   or block such cookies (low-memory eviction, third-party cookie blocking,
 *   private mode, aggressive background-tab cleanup), and the user gets
 *   bounced to /login even though they have a valid JWT. By making the
 *   Bearer token the primary auth source, the session survives any cookie
 *   loss.
 *
 * Login → POST /api/auth/login:
 *   - Server sets `auth_token` httpOnly cookie AND returns the JWT in body.
 *   - We save the JWT to `localStorage` (NOT sessionStorage) so it survives
 *     tab close on mobile browsers.
 *
 * Restore — on mount we call GET /api/auth/me with `Authorization: Bearer
 * <token>` from localStorage. We use raw `fetch` here (not httpClient) so
 * the axios response interceptor — which redirects to /login on 401 — can't
 * fire before we've had a chance to handle the result.
 *
 * After restore, all subsequent API calls go through httpClient, whose
 * request interceptor re-attaches the Bearer token from localStorage on every
 * call.
 *
 * Cross-origin screenshot auth:
 *   <img src="/screenshots/..."> tags don't carry httpOnly cookies on
 *   cross-origin loads, so `resolveScreenshotUrl` appends `?token=<jwt>`
 *   from localStorage.
 *
 * Logout → POST /api/auth/logout (server clears cookie) + drop local copy.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { AdminRole } from '@monitor/shared'
import { httpClient } from '@/lib/axios'
import { config } from '@/lib/env'

export interface AuthUser {
  id: number
  username: string
  role: AdminRole
  email?: string | null
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const TOKEN_STORAGE_KEY = 'admin_token'

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) ?? sessionStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } catch {
    /* localStorage disabled */
  }
}

function clearStoredToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
  } catch {
    /* localStorage disabled */
  }
  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY)
  } catch {
    /* sessionStorage disabled */
  }
}

/** Build a URL for an API request, handling relative + absolute baseURLs. */
function apiUrl(path: string): string {
  const base = config.API_URL
  if (!base) return path
  return base.endsWith('/') ? base + path.replace(/^\//, '') : `${base}${path}`
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Restore session: use raw fetch with Bearer token from localStorage.
  //
  // We bypass httpClient on purpose — its response interceptor redirects to
  // /login on any 401, which would fire here the moment the token expires
  // or the server has a brief hiccup, kicking the user out before we even
  // had a chance to read the error. With fetch we control the redirect
  // decision ourselves: only redirect if we're not on /login AND there's
  // no usable token.
  useEffect(() => {
    let cancelled = false
    const restore = async () => {
      const token = readStoredToken()
      if (!token) {
        if (!cancelled) setUser(null)
        return
      }
      try {
        const r = await fetch(apiUrl('/api/auth/me'), {
          method: 'GET',
          credentials: 'include', // also send the cookie if it exists
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
        if (!r.ok) {
          // 401 = token expired/invalid; drop it so the user gets a clean
          // login screen rather than retrying with a bad token.
          if (r.status === 401) clearStoredToken()
          if (!cancelled) setUser(null)
          return
        }
        const data = (await r.json()) as AuthUser
        if (!cancelled) setUser(data)
      } catch {
        // Network error — treat as not authenticated; user can retry login.
        if (!cancelled) setUser(null)
      }
    }
    restore().finally(() => {
      if (!cancelled) setIsLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    const r = await httpClient.post<{
      id: number
      username: string
      role: AdminRole
      expiresAt: string
      token: string
    }>('/api/auth/login', { username, password })
    // Server sets the auth_token cookie via Set-Cookie header (works on
    // desktop). For mobile, the Bearer token is what actually keeps the
    // session alive across visits, so persist it to localStorage.
    writeStoredToken(r.data.token)
    setUser({ id: r.data.id, username: r.data.username, role: r.data.role })
  }, [])

  const logout = useCallback(async () => {
    try {
      await httpClient.post('/api/auth/logout')
    } finally {
      clearStoredToken()
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
    }),
    [user, isLoading, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
